import { createContext, useContext, useEffect, useState } from 'react'
import defaultContent from '../content/defaultContent.json'

const ContentContext = createContext(defaultContent)

/**
 * Provider — mount once near the root of the app. Fetches /api/content
 * and falls back to the bundled defaultContent.json on any failure
 * (network, non-2xx, malformed JSON).
 *
 * The default is rendered immediately so first paint is never blocked
 * waiting on the fetch. When the fetch resolves successfully, content
 * swaps in via React state — pages re-render with the live content.
 */
export function ContentProvider({ children }) {
    const [content, setContent] = useState(defaultContent)

    useEffect(() => {
        let cancelled = false
        ;(async () => {
            try {
                const res = await fetch('/api/content')
                if (!res.ok) return
                const live = await res.json()
                if (cancelled) return
                if (live && typeof live === 'object' && live.schema_version === 1) {
                    setContent(live)
                }
            } catch {
                // network / parse failure → keep default (no-op)
            }
        })()
        return () => { cancelled = true }
    }, [])

    return <ContentContext.Provider value={content}>{children}</ContentContext.Provider>
}

export function useContent() {
    return useContext(ContentContext)
}
